import { Collapsible } from '@chakra-ui/react';
import { UserRoleEnum } from '@monetize/types/app';
import {
  QuoteTemplateSettingsConfig,
  SettingAccessScopeEnum,
  SettingVisibilityEnum,
} from '@monetize/types/experience-config';
import {
  Control,
  Controller,
  FieldPath,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form';
import { USER_ROLES_DISPLAY } from '../../../constants/roles';
import { objectToSelectOptions } from '../../../utils/misc';
import {
  MBox,
  MFlex,
  MRadio,
  MRadioGroup,
  MStack,
  MText,
} from '../../Monetize';
import { MCustomSelect } from '../../Monetize/MCustomSelect/MCustomSelect';
import {
  QUOTE_TEMPLATE_PERMISSION_ROLES,
  SETTING_ACCESS_SCOPE_LABEL,
  SETTING_VISIBILITY_DISPLAY,
} from './quoteTemplateSettings.metadata';
import { applyVisibilityToAllRoles } from './quoteTemplateSettings.utils';

const VISIBILITY_OPTIONS = objectToSelectOptions(SETTING_VISIBILITY_DISPLAY);

interface QuoteTemplateAccessControlProps {
  settingKey: string;
  control: Control<QuoteTemplateSettingsConfig>;
  setValue: UseFormSetValue<QuoteTemplateSettingsConfig>;
  isDisabled?: boolean;
}

/**
 * Renders the Access Scope (All Roles / Specific Roles) + Visibility controls
 * for a single quote-template setting. Bound to the experience-config
 * permissions form.
 */
export const QuoteTemplateAccessControl = ({
  settingKey,
  control,
  setValue,
  isDisabled,
}: QuoteTemplateAccessControlProps) => {
  const accessScopeName =
    `settings.${settingKey}.accessScope` as FieldPath<QuoteTemplateSettingsConfig>;
  // The names are dynamic record paths, so RHF widens the value types; the
  // resolved runtime values are the accessScope enum and role-visibility map.
  const accessScope = useWatch({
    control,
    name: accessScopeName,
  }) as unknown as SettingAccessScopeEnum;
  const roleVisibility = useWatch({
    control,
    name: `settings.${settingKey}.roleVisibility` as FieldPath<QuoteTemplateSettingsConfig>,
  }) as unknown as
    | Partial<Record<UserRoleEnum, SettingVisibilityEnum>>
    | undefined;

  const isSpecificRoles = accessScope === SettingAccessScopeEnum.SPECIFIC_ROLES;

  // Doubles as a summary: shows the shared value when every role matches,
  // otherwise clears so the placeholder indicates a mixed configuration.
  const distinctVisibilities = new Set(
    QUOTE_TEMPLATE_PERMISSION_ROLES.map(
      (role) => roleVisibility?.[role] ?? SettingVisibilityEnum.EDITABLE,
    ),
  );
  const bulkVisibility =
    distinctVisibilities.size === 1 ? [...distinctVisibilities][0] : '';

  const handleApplyToAllRoles = (visibility: SettingVisibilityEnum) => {
    applyVisibilityToAllRoles(setValue, settingKey, visibility);
  };

  return (
    <MBox>
      <MFlex alignItems="center" justifyContent="space-between" mb={3}>
        <MText fontWeight="600">Access Scope</MText>
        <Controller
          control={control}
          name={accessScopeName}
          render={({ field: { value, onChange, ...rest } }) => (
            <MRadioGroup
              value={value as unknown as SettingAccessScopeEnum}
              onChange={(val) => onChange(val)}
              isDisabled={isDisabled}
              {...rest}
            >
              <MStack gap={4} direction="row">
                <MRadio value={SettingAccessScopeEnum.ALL_ROLES}>
                  {SETTING_ACCESS_SCOPE_LABEL.ALL_ROLES}
                </MRadio>
                <MRadio value={SettingAccessScopeEnum.SPECIFIC_ROLES}>
                  {SETTING_ACCESS_SCOPE_LABEL.SPECIFIC_ROLES}
                </MRadio>
              </MStack>
            </MRadioGroup>
          )}
        />
      </MFlex>

      {!isSpecificRoles && (
        <MFlex alignItems="center" justifyContent="space-between">
          <MText>Visibility</MText>
          <MBox minW="200px">
            <Controller
              control={control}
              name={
                `settings.${settingKey}.allRolesVisibility` as FieldPath<QuoteTemplateSettingsConfig>
              }
              render={({ field }) => (
                <MCustomSelect
                  items={VISIBILITY_OPTIONS}
                  isDisabled={isDisabled}
                  {...field}
                />
              )}
            />
          </MBox>
        </MFlex>
      )}

      {/* Collapse clips its content (overflow: hidden), cutting off the select
          focus halos at its edges. The inner padding gives the halos clearance
          inside the clip box and the wrapper's negative margins cancel it out,
          so the content lines up with the rest of the card. The 8px clearance
          also lets the "Set all roles to" band bleed wider than the content. */}
      <MBox mx="-8px" my={isSpecificRoles ? '-8px' : 0}>
        <Collapsible.Root open={isSpecificRoles} lazyMount unmountOnExit>
          <Collapsible.Content>
            <MBox p="8px">
              {/* Band bleeds into the clearance (mx) and pads back (px) so its
                  content stays column-aligned with the role rows below. */}
              <MFlex
                alignItems="center"
                justifyContent="space-between"
                bg="bg.muted"
                borderRadius="md"
                mx="-8px"
                px="8px"
                py={2}
                mb={2}
              >
                <MText>Set all roles to</MText>
                <MBox minW="200px">
                  <MCustomSelect
                    data-testid={`quoteTemplateSetting-applyToAll-${settingKey}`}
                    items={VISIBILITY_OPTIONS}
                    value={bulkVisibility}
                    placeholder="Mixed"
                    isDisabled={isDisabled}
                    onChange={handleApplyToAllRoles}
                  />
                </MBox>
              </MFlex>

              <MStack gap={2}>
                {QUOTE_TEMPLATE_PERMISSION_ROLES.map((role) => (
                  <MFlex
                    key={role}
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <MText>{USER_ROLES_DISPLAY[role]}</MText>
                    <MBox minW="200px">
                      <Controller
                        control={control}
                        name={
                          `settings.${settingKey}.roleVisibility.${role}` as FieldPath<QuoteTemplateSettingsConfig>
                        }
                        render={({ field: { value, ...rest } }) => (
                          <MCustomSelect
                            items={VISIBILITY_OPTIONS}
                            value={value ?? SettingVisibilityEnum.EDITABLE}
                            isDisabled={isDisabled}
                            {...rest}
                          />
                        )}
                      />
                    </MBox>
                  </MFlex>
                ))}
              </MStack>
            </MBox>
          </Collapsible.Content>
        </Collapsible.Root>
      </MBox>
    </MBox>
  );
};
